import React, { useEffect, useState } from "react";
import api, { fmt, fmtDate } from "@/lib/api";
import { PageHeader } from "@/components/layout/PageHeader";
import { Plus, ArrowsHorizontal, Calculator, CloudArrowDown } from "@phosphor-icons/react";

const CCYS = ["USD", "EUR", "GBP", "AED", "INR", "JPY", "CHF", "SGD"];

export default function FxPage() {
  const [rates, setRates] = useState([]);
  const [deals, setDeals] = useState([]);
  const [accts, setAccts] = useState([]);
  const [rateForm, setRateForm] = useState({ base: "USD", quote: "EUR", rate: "" });
  const [dealForm, setDealForm] = useState({
    deal_date: new Date().toISOString().slice(0, 10),
    buy_currency: "EUR", buy_amount: "", sell_currency: "USD", sell_amount: "",
    bank_account_id: "", notes: "",
  });
  const [revalBase, setRevalBase] = useState("USD");
  const [revalResult, setRevalResult] = useState(null);
  const [syncMsg, setSyncMsg] = useState("");

  const load = async () => {
    const [r, d, a] = await Promise.all([
      api.get("/fx-rates"), api.get("/fx-deals"), api.get("/bank-accounts"),
    ]);
    setRates(r.data); setDeals(d.data); setAccts(a.data);
  };
  useEffect(() => { load(); }, []);

  const submitRate = async (e) => {
    e.preventDefault();
    await api.post("/fx-rates", { ...rateForm, rate: parseFloat(rateForm.rate), source: "manual" });
    setRateForm({ ...rateForm, rate: "" }); load();
  };
  const submitDeal = async (e) => {
    e.preventDefault();
    await api.post("/fx-deals", {
      ...dealForm, buy_amount: parseFloat(dealForm.buy_amount),
      sell_amount: parseFloat(dealForm.sell_amount),
      bank_account_id: dealForm.bank_account_id || null,
    });
    setDealForm({ ...dealForm, buy_amount: "", sell_amount: "" }); load();
  };
  const runReval = async () => {
    const { data } = await api.post("/revaluation/run", { base_currency: revalBase });
    setRevalResult(data); load();
  };
  const syncBloomberg = async () => {
    try {
      const { data } = await api.post("/fx-rates/sync-bloomberg");
      setSyncMsg(data.message || "Synced");
      load();
    } catch {
      setSyncMsg("Sync failed");
    }
  };

  // Latest rates grouped by base->quote pair
  const latestMap = {};
  rates.forEach((r) => {
    const k = `${r.base}/${r.quote}`;
    if (!latestMap[k] || latestMap[k].as_of < r.as_of) latestMap[k] = r;
  });
  const latest = Object.values(latestMap);

  return (
    <div>
      <PageHeader
        eyebrow="FX & Revaluation"
        title="Currency Exchange & MTM"
        subtitle="Manage FX rates, book FX deals, and run mark-to-market revaluation across the book."
        testid="fx-title"
        actions={
          <button onClick={syncBloomberg} data-testid="sync-bloomberg" className="border border-gray-300 px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-gray-50 inline-flex items-center gap-2">
            <CloudArrowDown size={14} weight="bold" /> Sync Bloomberg
          </button>
        }
      />

      {syncMsg && (
        <div className="mx-8 mt-6 border border-gray-300 bg-gray-50 px-4 py-3 text-xs font-mono" data-testid="sync-msg">{syncMsg}</div>
      )}

      <div className="p-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* FX Rates */}
        <div className="border border-gray-200 bg-white">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Rates</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Live FX Table</h3>
            </div>
            <ArrowsHorizontal size={18} />
          </div>
          <form onSubmit={submitRate} className="px-6 py-4 border-b border-gray-200 grid grid-cols-4 gap-2 items-end" data-testid="fx-rate-form">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Base</label>
              <select value={rateForm.base} onChange={(e) => setRateForm({ ...rateForm, base: e.target.value })} data-testid="rate-base" className="w-full border border-gray-300 px-2 py-1.5 text-xs">{CCYS.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Quote</label>
              <select value={rateForm.quote} onChange={(e) => setRateForm({ ...rateForm, quote: e.target.value })} data-testid="rate-quote" className="w-full border border-gray-300 px-2 py-1.5 text-xs">{CCYS.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Rate</label>
              <input type="number" step="any" required value={rateForm.rate} onChange={(e) => setRateForm({ ...rateForm, rate: e.target.value })} data-testid="rate-value" className="w-full border border-gray-300 px-2 py-1.5 text-xs font-mono" />
            </div>
            <button type="submit" data-testid="rate-submit" className="bg-[#0020C2] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider"><Plus size={12} weight="bold" className="inline" /> Add</button>
          </form>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full text-sm" data-testid="rate-table">
              <thead className="bg-gray-50 sticky top-0">
                <tr><th className="px-4 py-2 text-left text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">Pair</th>
                  <th className="px-4 py-2 text-right text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">Rate</th>
                  <th className="px-4 py-2 text-left text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">As Of</th>
                  <th className="px-4 py-2 text-left text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300">Src</th>
                </tr>
              </thead>
              <tbody>
                {latest.map((r) => (
                  <tr key={`${r.base}-${r.quote}-${r.as_of}`} className="border-b border-gray-100">
                    <td className="px-4 py-2 font-mono font-bold">{r.base}/{r.quote}</td>
                    <td className="px-4 py-2 text-right font-mono">{r.rate.toFixed(4)}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-500">{fmtDate(r.as_of)}</td>
                    <td className="px-4 py-2 text-[10px] uppercase font-bold tracking-wider">{r.source}</td>
                  </tr>
                ))}
                {latest.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-500">No rates.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* MTM Revaluation */}
        <div className="border border-gray-200 bg-white">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">MTM</div>
              <h3 className="text-xl font-black tracking-tight mt-0.5">Mark-to-Market Revaluation</h3>
            </div>
            <Calculator size={18} />
          </div>
          <div className="px-6 py-4 flex items-end gap-3 border-b border-gray-200">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Base Currency</label>
              <select value={revalBase} onChange={(e) => setRevalBase(e.target.value)} data-testid="reval-base" className="block border border-gray-300 px-3 py-2 text-sm font-mono mt-1">
                {CCYS.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <button onClick={runReval} data-testid="reval-run" className="bg-[#0020C2] text-white px-4 py-2 text-xs font-bold uppercase tracking-wider hover:bg-[#001AA3]">Run revaluation</button>
          </div>
          <div className="px-6 py-4">
            {revalResult ? (
              <>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Result</div>
                <div className={`text-3xl font-black font-mono mt-2 ${revalResult.total_pnl < 0 ? "text-[#DC2626]" : "text-gray-900"}`} data-testid="reval-pnl">
                  {revalResult.total_pnl > 0 ? "+" : ""}{fmt(revalResult.total_pnl, revalResult.base_currency)}
                </div>
                <table className="w-full text-sm mt-4" data-testid="reval-rows">
                  <thead><tr className="text-left">
                    {["Ccy","Exposure","Rate","Base Value","PnL"].map((h) => <th key={h} className={`px-2 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${["Exposure","Rate","Base Value","PnL"].includes(h) ? "text-right" : ""}`}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {revalResult.rows.map((r) => (
                      <tr key={r.currency} className="border-b border-gray-100">
                        <td className="px-2 py-2 font-mono font-bold">{r.currency}</td>
                        <td className="px-2 py-2 text-right font-mono">{fmt(r.amount)}</td>
                        <td className="px-2 py-2 text-right font-mono text-xs">{r.rate ? r.rate.toFixed(4) : "—"}</td>
                        <td className="px-2 py-2 text-right font-mono">{fmt(r.base_value)}</td>
                        <td className={`px-2 py-2 text-right font-mono ${r.pnl < 0 ? "text-[#DC2626]" : ""}`}>{fmt(r.pnl)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <div className="text-sm text-gray-500 py-6">Run revaluation to mark all open positions to current rates and post unrealised PnL.</div>
            )}
          </div>
        </div>

        {/* FX Deals */}
        <div className="border border-gray-200 bg-white lg:col-span-2">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Currency Exchange</div>
            <h3 className="text-xl font-black tracking-tight mt-0.5">FX Deals</h3>
          </div>
          <form onSubmit={submitDeal} className="px-6 py-4 border-b border-gray-200 grid grid-cols-2 md:grid-cols-6 gap-3 items-end" data-testid="fx-deal-form">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Date</label>
              <input type="date" value={dealForm.deal_date} onChange={(e) => setDealForm({ ...dealForm, deal_date: e.target.value })} data-testid="deal-date" className="w-full border border-gray-300 px-2 py-1.5 text-xs font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Buy Ccy</label>
              <select value={dealForm.buy_currency} onChange={(e) => setDealForm({ ...dealForm, buy_currency: e.target.value })} data-testid="deal-buy-ccy" className="w-full border border-gray-300 px-2 py-1.5 text-xs">{CCYS.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Buy Amt</label>
              <input type="number" step="any" required value={dealForm.buy_amount} onChange={(e) => setDealForm({ ...dealForm, buy_amount: e.target.value })} data-testid="deal-buy-amt" className="w-full border border-gray-300 px-2 py-1.5 text-xs font-mono" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Sell Ccy</label>
              <select value={dealForm.sell_currency} onChange={(e) => setDealForm({ ...dealForm, sell_currency: e.target.value })} data-testid="deal-sell-ccy" className="w-full border border-gray-300 px-2 py-1.5 text-xs">{CCYS.map((c) => <option key={c}>{c}</option>)}</select>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600">Sell Amt</label>
              <input type="number" step="any" required value={dealForm.sell_amount} onChange={(e) => setDealForm({ ...dealForm, sell_amount: e.target.value })} data-testid="deal-sell-amt" className="w-full border border-gray-300 px-2 py-1.5 text-xs font-mono" />
            </div>
            <button type="submit" data-testid="deal-submit" className="bg-[#0020C2] text-white px-3 py-2 text-[10px] font-bold uppercase tracking-wider"><Plus size={12} weight="bold" className="inline" /> Book</button>
          </form>
          <table className="w-full text-sm" data-testid="deals-table">
            <thead className="bg-gray-50"><tr className="text-left">
              {["Date","Buy","Sell","Rate","Notes"].map((h) => <th key={h} className={`px-4 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500 border-b border-gray-300 ${h === "Rate" ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.id} className="border-b border-gray-100">
                  <td className="px-4 py-2 font-mono text-xs">{fmtDate(d.deal_date)}</td>
                  <td className="px-4 py-2 font-mono"><span className="font-bold">{fmt(d.buy_amount)}</span> {d.buy_currency}</td>
                  <td className="px-4 py-2 font-mono"><span className="font-bold">{fmt(d.sell_amount)}</span> {d.sell_currency}</td>
                  <td className="px-4 py-2 text-right font-mono">{d.rate.toFixed(4)}</td>
                  <td className="px-4 py-2 text-gray-600 text-xs">{d.notes}</td>
                </tr>
              ))}
              {deals.length === 0 && <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-500">No FX deals booked.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
